import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import i18next from 'i18next';
import { loadCatalog } from '@/lib/i18n';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
    applyDocumentLanguage,
    getLocale,
    LOCALE_LABELS,
    setLocale,
    SUPPORTED_LOCALES,
    type SupportedLocale,
} from '@/lib/locale';
import { api } from '@/services/ApiService';

/**
 * Language toggle. Phase 0.5 of the Spanish rollout — see `docs/plans/spanish-translation/`.
 *
 * Switching locale has to do three things, and missing any one of them produces a bug that does
 * not reproduce locally:
 *
 * 1. Persist the choice, so it survives a reload.
 * 2. Reset the RTK Query cache. RTK Query keys on endpoint name plus serialized args, and
 *    `Accept-Language` is a header, so it is NOT part of the cache key. Without the reset every
 *    already-fetched query keeps serving its previous-language response with no refetch, and it
 *    looks like the backend ignored the switch.
 * 3. Update `<html lang>`, so assistive technology announces the page in the right language.
 *
 * What actually changes today is ZigBot, which generates replies in the selected language. Static
 * copy and database content remain English until the later phases land. The label below says so
 * rather than implying the whole product is translated.
 */
export const LanguageSwitcher = ({ className }: { className?: string }) => {
    const dispatch = useDispatch();
    const { featureFlags } = useFeatureFlags();
    const enabled = featureFlags.FEATURE_FLAG_SPANISH_TRANSLATION === true;
    const [locale, setLocaleState] = useState<SupportedLocale>(getLocale);

    // Guarded on `enabled`, not just placed above the early return: React runs registered effects
    // on mount even when a render returns null, so without this check a disabled switcher would
    // still rewrite <html lang>. That is the one thing this component does that escapes its own
    // markup, and claiming a language the page is not actually written in misleads screen readers
    // and invites the browser's own translation prompt.
    useEffect(() => {
        if (!enabled) {
            return;
        }
        applyDocumentLanguage(locale);
    }, [enabled, locale]);

    const handleChange = useCallback((next: string) => {
        // ToggleGroup emits '' when the active item is clicked again. Ignore it: a language
        // toggle has no "off" state.
        if (!next || next === locale) {
            return;
        }

        const nextLocale = next as SupportedLocale;
        setLocale(nextLocale);
        setLocaleState(nextLocale);
        // Static interface copy: fetch the catalogue for this locale (a no-op if already loaded),
        // then switch. i18next re-renders its consumers.
        void loadCatalog(nextLocale).then(() => i18next.changeLanguage(nextLocale));
        // Server-rendered content: headers are not part of RTK Query's cache key, so without this
        // every fetched query keeps serving its previous-language response with no refetch.
        dispatch(api.util.resetApiState());
    }, [dispatch, locale]);

    // Off unless explicitly enabled. Spanish is incomplete: only the surfaces wired so far respond,
    // so showing the toggle would promise more than it delivers. Turn the flag on per environment
    // as coverage grows.
    if (!enabled) {
        return null;
    }

    return (
        <div className={className}>
            <ToggleGroup
                type="single"
                value={locale}
                onValueChange={handleChange}
                aria-label="Language"
            >
                {SUPPORTED_LOCALES.map(code => (
                    <ToggleGroupItem key={code} value={code} aria-label={LOCALE_LABELS[code]}>
                        <span className="notranslate" translate="no">{LOCALE_LABELS[code]}</span>
                    </ToggleGroupItem>
                ))}
            </ToggleGroup>
        </div>
    );
};
